import type { Price } from '@backmarket/http-api'
import { Currency } from '@backmarket/http-api/src/standards/Currency'
import {
  BCP_47_LOCALES,
  type Bcp47Locale,
} from '@backmarket/http-api/src/standards/Locale'

function isPriceModel(value: Price | string | number): value is Price {
  return typeof value !== 'string' && typeof value !== 'number'
}

export type CreatePriceFormatterOptions = Intl.NumberFormatOptions & {
  currency?: Currency
  symbol?: boolean
}

export default function createPriceFormatter(
  locale: Bcp47Locale,
  defaultCurrency: Currency,
) {
  return (
    amountOrPrice?: Price | string | number,
    options: CreatePriceFormatterOptions = {},
  ) => {
    if (
      // We want all falsy values to return an empty string (no price to show).
      // Except for 0, which is a valid price.
      (!amountOrPrice && amountOrPrice !== 0) ||
      JSON.stringify(amountOrPrice) === '{}'
    ) {
      return ''
    }

    const safeCurrency = options.currency || defaultCurrency

    /**
     * @FIXME - After Pastrami migration
     * We should always receive a price.
     * We should try to deprecate support for argument of type string and number.
     */
    const price = isPriceModel(amountOrPrice)
      ? amountOrPrice
      : {
          amount: amountOrPrice.toString(),
          currency: safeCurrency,
        }

    const { symbol = true, ...intlNumberFormatOptions } = options

    const optionsWithOverrides: Intl.NumberFormatOptions = {
      currency: price.currency,
      style: 'currency',
      ...intlNumberFormatOptions,
    }

    const numberFormat = new Intl.NumberFormat(locale, optionsWithOverrides)

    const priceWithCurrencySymbol = numberFormat.format(
      parseFloat(price.amount),
    )

    if (symbol) {
      // Native implementation behavior is to display A$XX only for en-us locale
      // but product requirements for backmarket.com.au is to use A$XX for any locale
      if (
        optionsWithOverrides.currency === Currency.AUD &&
        locale === BCP_47_LOCALES.en_AU
      ) {
        return `A${priceWithCurrencySymbol}`
      }

      // Replace the yen character to always have the same
      // Safari Intl.NumberFormat format differently from other browsers (yen symbol is not the same unicode) causing hydration error
      if (
        optionsWithOverrides.currency === Currency.JPY &&
        locale === BCP_47_LOCALES.ja_JP
      ) {
        const priceFormatted = numberFormat
          .formatToParts(parseFloat(price.amount))
          .map(({ type, value }) => {
            switch (type) {
              case 'currency':
                return '¥'
              default:
                return value
            }
          })
          .reduce((string, part) => string + part)

        return priceFormatted
      }

      return priceWithCurrencySymbol
    }

    const removeCurrencySymbolRegex = /[^\d.,-\s]+/g

    return priceWithCurrencySymbol.replace(removeCurrencySymbolRegex, '').trim()
  }
}
